import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  overridePrescription,
  PRESCRIPTION_OVERRIDE_REASONS,
} from '../../actions/matters/overridePrescription';

interface PrescriptionOverrideModalProps {
  open: boolean;
  onClose: () => void;
  matterId: string;
  currentDate: string | undefined;
  onSuccess: () => void;
}

const PrescriptionOverrideModal: React.FC<PrescriptionOverrideModalProps> = ({
  open,
  onClose,
  matterId,
  currentDate,
  onSuccess,
}) => {
  const [date, setDate] = useState(currentDate || '');
  const [reason, setReason] = useState(PRESCRIPTION_OVERRIDE_REASONS[0] || '');
  const [customReason, setCustomReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (!date) {
      setError('Please select a prescription date.');
      return;
    }
    const finalReason =
      reason === PRESCRIPTION_OVERRIDE_REASONS[PRESCRIPTION_OVERRIDE_REASONS.length - 1]
        ? customReason.trim()
        : reason;
    if (!finalReason) {
      setError('Please provide a reason for the override.');
      return;
    }

    setSaving(true);
    try {
      await overridePrescription(matterId, date, finalReason);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save override.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 460 },
          bgcolor: 'var(--s2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: 'var(--s1)',
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-sm)',
              bgcolor: 'color-mix(in srgb, var(--teal) 14%, transparent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalendarTodayIcon sx={{ color: 'var(--teal)', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--t1)', lineHeight: 1.3 }}
            >
              Override Prescription Date
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'var(--t3)' }}>
              Manually set a new prescription deadline
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography
              sx={{ fontSize: '0.76rem', color: 'var(--t3)', fontWeight: 500, mb: 0.75 }}
            >
              New prescription date
            </Typography>
            <TextField
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box>
            <Typography
              sx={{ fontSize: '0.76rem', color: 'var(--t3)', fontWeight: 500, mb: 0.75 }}
            >
              Reason for override
            </Typography>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value as string)}
              fullWidth
              size="small"
            >
              {PRESCRIPTION_OVERRIDE_REASONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {reason === PRESCRIPTION_OVERRIDE_REASONS[PRESCRIPTION_OVERRIDE_REASONS.length - 1] && (
            <TextField
              multiline
              rows={2}
              placeholder="Describe the reason..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              size="small"
              fullWidth
            />
          )}

          {error && (
            <Typography sx={{ color: 'var(--err)', fontSize: '0.78rem' }}>{error}</Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button
              onClick={onClose}
              disabled={saving}
              sx={{
                textTransform: 'none',
                color: 'var(--t2)',
                fontSize: '0.82rem',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
              sx={{
                bgcolor: 'var(--teal)',
                color: 'var(--bg)',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.82rem',
                borderRadius: 'var(--radius-sm)',
                '&:hover': { bgcolor: 'var(--teal-mid)' },
                '&.Mui-disabled': { bgcolor: 'var(--s3)', color: 'var(--t4)' },
              }}
            >
              {saving ? 'Saving...' : 'Save Override'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PrescriptionOverrideModal;
